<template>
  <VTDropDownPill
    :is-enabled="editable"
    :disable-save-button="!isOfferTimerValid"
    transition="scale-fade-left"
    @click="handleClickOnDropDown"
    @on-click-outside="handleReset"
    @clear="handleReset"
    @confirm="handleSave"
  >
    <template #dropDownHeader>
      <div
        :class="[
          $style.wrapper,
          { [$style.clickable]: editable },
          { [$style.activeTimer]: displayCountDown },
          { [$style.offTimer]: !displayCountDown },
        ]"
      >
        <VdIcon store="fa" name="stopwatch" size="large" :color="iconColor"></VdIcon>
        {{ label }}
        <VdIcon v-if="editable" store="fa" name="chevron-down" size="large" :color="iconColor" />
      </div>
    </template>
    <template #dropdown-content>
      <div :class="$style.container">
        <div class="p-field-checkbox" :class="$style.checkboxWrapper">
          <Checkbox v-model="offer.displayCountdown" input-id="countdown" name="countdown" :binary="true" />
          <label for="countdown" class="ml-2">Display countdown</label>
        </div>
        <div v-if="offer.displayCountdown" :class="$style.timerSection">
          <RadioButton v-model="offer.timer.timerType" input-id="relative-timer" name="timerType" value="RELATIVE" />
          <label for="relative-timer" class="ml-2">Relative</label>
          <RadioButton v-model="offer.timer.timerType" input-id="absolute-timer" name="timerType" value="ABSOLUTE" />
          <label for="absolute-timer" class="ml-2">Absolute</label>
        </div>
        <div v-if="offer.displayCountdown">
          <div v-if="!isAbsoluteTimeSelected" :class="$style.timerPickerSection">
            <InputNumber
              id="dayCount"
              v-model="offer.timer.dayCount"
              type="number"
              :min="0"
              suffix=" Days"
              :class="$style.timerInput"
            />
            <InputNumber
              id="hourCount"
              v-model="offer.timer.hourCount"
              type="number"
              :min="0"
              :max="23"
              suffix=" Hours"
              :class="$style.timerInput"
            />
            <InputNumber
              id="minuteCount"
              v-model="offer.timer.minuteCount"
              type="number"
              :min="0"
              :max="59"
              suffix=" Min"
              :class="$style.timerInput"
            />
            <div id="timerDurationParentRef" ref="timerDurationParentRef"></div>
          </div>
          <div v-if="!isOfferTimerValid" :class="$style.error">
            {{ errorMessage }}
          </div>
        </div>
      </div>
    </template>
  </VTDropDownPill>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import VTDropDownPill from "@/components/VTDropdownPill";
import Checkbox from "primevue/checkbox";
import InputNumber from "primevue/inputnumber";
import RadioButton from "primevue/radiobutton";
import type { PropType } from "vue";
import { computed, reactive, ref } from "vue";

enum TimerType {
  ABSOLUTE = "ABSOLUTE",
  RELATIVE = "RELATIVE",
}
const props = defineProps({
  displayCountDown: {
    type: Boolean,
    required: true,
  },
  timerType: {
    type: String as PropType<TimerType>,
    required: true,
  },
  timerDuration: {
    type: String,
    required: false,
    default: null,
  },
  startDate: String,
  endDate: String,
  editable: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const timerDurationParentRef = ref(null);

const offer = reactive({
  displayCountdown: props.displayCountDown,
  timer: reactive({
    timerType: props.timerType,
    dayCount: parseTimer(props.timerDuration).dayCount,
    hourCount: parseTimer(props.timerDuration).hourCount,
    minuteCount: parseTimer(props.timerDuration).minuteCount,
  }),
});

const iconColor = computed(() => {
  if (props.displayCountDown) {
    return "#337DCC";
  }
  return "";
});

const label = computed(() => {
  if (!props.displayCountDown) {
    return "Off";
  }
  if (props.timerType === TimerType.ABSOLUTE) {
    return "Absolute";
  }
  return formatTimerLabel(props.timerDuration);
});

const errorMessage = computed(() => {
  if (!offer.timer.dayCount && !offer.timer.hourCount && !offer.timer.minuteCount) {
    return "Timer duration is required";
  }

  return "Set a timer duration shorter than the total duration of the offer";
});

const isAbsoluteTimeSelected = computed(() => offer.timer.timerType === TimerType.ABSOLUTE);

const emits = defineEmits(["update"]);

function formatTimerLabel(time: string) {
  const parsedTimer = parseTimer(time);

  return `${parsedTimer.dayCount}d ${parsedTimer.hourCount}h ${parsedTimer.minuteCount}m`;
}

function parseTimer(timerDuration: string | undefined) {
  if (!timerDuration) {
    return {
      dayCount: 0,
      hourCount: 0,
      minuteCount: 0,
    };
  }

  const parts = timerDuration.split("::");

  const dayCount = parts.length === 2 ? parseInt(parts[0], 10) : 0;

  const time = parts.length === 1 ? parts[0] : parts[1];

  const hourCount = parseInt(time.split(":", 10)[0], 10);

  const minuteCount = parseInt(time.split(":", 10)[1], 10);

  return {
    dayCount,
    hourCount,
    minuteCount,
  };
}

function formatTimerDuration({
  dayCount,
  hourCount,
  minuteCount,
}: {
  dayCount: number;
  hourCount: number;
  minuteCount: number;
}) {
  return `${dayCount || 0}::${(hourCount || 0).toString().padStart(2, "0")}:${(minuteCount || 0)
    .toString()
    .padStart(2, "0")}:00`;
}

function handleReset() {
  offer.timer = {
    timerType: props.timerType,
    ...parseTimer(props.timerDuration),
  };

  offer.displayCountdown = props.displayCountDown;
}

function handleSave() {
  const payload = {
    displayCountDown: offer.displayCountdown,
    timerType: !offer.displayCountdown ? TimerType.ABSOLUTE : offer.timer.timerType,
    timerDuration: offer.timer.timerType === TimerType.RELATIVE ? formatTimerDuration(offer.timer) : null,
  };
  emits("update", payload);
}

function handleClickOnDropDown(event) {
  event.stopPropagation();
}

const isOfferTimerValid = computed(() => {
  if (offer.displayCountdown && offer.timer.timerType === TimerType.RELATIVE) {
    if (!offer.timer.dayCount && !offer.timer.hourCount && !offer.timer.minuteCount) {
      return false;
    }
    if (offer.timer.hourCount > 23 || offer.timer.minuteCount > 59) {
      return false;
    }
    const millisecondsPerDay = 86_400_000;
    const maxPossibleTime = Math.max(
      new Date(props.endDate || "").getTime() - new Date(props.startDate || "").getTime() || 0,
      millisecondsPerDay
    );
    const timeValue =
      offer.timer.dayCount * millisecondsPerDay + offer.timer.hourCount * 3_600_000 || offer.timer.minuteCount * 60_000;

    return timeValue <= maxPossibleTime;
  }

  return true;
});
</script>
<style module>
.wrapper {
  width: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px 4px 8px;
  border-radius: 16px;
  gap: 6px;
  position: relative;
}
.clickable {
  cursor: pointer;
}
.offTimer {
  background-color: #e9e9ea;
}
.activeTimer {
  background-color: var(--vd-color-blue-light-9);
  color: var(--vd-color-blue-dark-3);
}
.container {
  padding: 4px;
  display: flex;
  flex-direction: column;
  width: 250px;
  gap: 8px;
}
.checkboxWrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 12px;
}
.timerSection {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin: 8px 8px;
}

.timerInput {
  margin-left: 0.5rem;
}

.timerInput input {
  font-size: 0.9rem;
  padding: 0.5rem;
  width: 5rem;
  height: 2.5rem;
  text-align: right;
}

.timerPickerSection {
  margin-bottom: 1rem;
  width: 250px;
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.timerPickerDisabled {
  background-color: #f4f5f7;
  color: #d1d5db;
  cursor: not-allowed;
}
.cursor-pointer {
  cursor: pointer;
}

.fixedTopLeft {
  top: inherit !important;
  left: inherit !important;
}
.error {
  font-size: 12px;
  color: #f87171;
  margin-bottom: 1rem;
}
</style>
